import { useEffect, useRef, useState } from 'react'

import { getRentalPortalApplicantsPage } from '../../../../../api'
import { EMPTY_OBJECT } from '../../../../../constants/common'

import {
  getApplicantsBoardColumnBody,
  getApplicantsBoardReadKey,
  getApplicantsBoardStages,
  getIsApplicantsBoardRefetching,
  toApplicantsBoardColumns,
} from './useRentalPortalApplicantsBoard.utils'

export function useRentalPortalApplicantsBoard({
  filterValues,
  isEnabled,
  ordering,
}) {
  const [refetchGeneration, setRefetchGeneration] = useState(0)
  const [result, setResult] = useState(null)

  const readGenerationRef = useRef(0)

  const readKey = getApplicantsBoardReadKey({ filterValues, ordering })

  async function handleReadBoard() {
    readGenerationRef.current += 1

    const generation = readGenerationRef.current
    const stages = getApplicantsBoardStages(filterValues)

    return Promise.all(
      stages.map(stage =>
        getRentalPortalApplicantsPage(
          getApplicantsBoardColumnBody({ filterValues, ordering, stage })
        )
      )
    )
      .then(responses => {
        const isCurrentRead = generation === readGenerationRef.current

        if (isCurrentRead) {
          setResult({
            columns: toApplicantsBoardColumns({ responses, stages }),
            generation,
            hasError: false,
            key: readKey,
          })
        }

        return isCurrentRead
      })
      .catch(() => {
        if (generation === readGenerationRef.current) {
          setResult(prevResult => ({
            ...prevResult,
            generation,
            hasError: true,
            key: readKey,
          }))
        }

        return false
      })
  }

  useEffect(() => {
    if (isEnabled) {
      handleReadBoard()
    }

    return () => {
      readGenerationRef.current += 1
    }
  }, [isEnabled, readKey])

  async function handleReloadBoard() {
    setRefetchGeneration(readGenerationRef.current + 1)

    return handleReadBoard()
  }

  function handleResetBoard() {
    setRefetchGeneration(0)
    setResult(null)
  }

  const isBoardRefetching = getIsApplicantsBoardRefetching({
    refetchGeneration,
    result,
  })

  const isCurrentResult = result?.key === readKey

  return {
    boardColumns: result?.columns ?? EMPTY_OBJECT,
    handleReloadBoard,
    handleResetBoard,
    hasBoardError: isCurrentResult && result.hasError,
    isBoardLoading: !isCurrentResult || isBoardRefetching,
    isBoardRefetching,
  }
}
